import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Snackbar } from '@mui/material';
import MainLayout from './components/layout/MainLayout';
import { testService } from './services';
import './Pentest.css';

// Create a theme instance with enhanced dark mode
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#9D5CFF', // Brighter purple for better visibility
      light: '#B57BFF',
      dark: '#7E45CC',
    },
    secondary: {
      main: '#80CFFF', // Brighter blue for contrast
      light: '#A7DDFF',
      dark: '#5B9FFF',
    },
    background: {
      default: '#121212', // Dark background
      paper: '#1E1E2E',   // Slightly more saturated for better contrast
    },
    text: {
      primary: '#FFFFFF', // Brighter white for better readability
      secondary: '#CCCCCC', // Lighter gray for secondary text
    },
    divider: 'rgba(255, 255, 255, 0.15)', // Slightly more visible dividers
    error: {
      main: '#FF5252',
    },
    warning: {
      main: '#FFB74D',
    },
    info: {
      main: '#64B5F6',
    },
    success: {
      main: '#81C784',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h6: {
      fontWeight: 600, // More visible headings
    },
    subtitle1: {
      fontWeight: 600, // More visible subtitles
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)', // Enhanced shadows for depth
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500, // Slightly bolder buttons
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500, // Bolder text in chips
        },
        outlined: {
          borderWidth: 1.5, // More visible borders for outlined chips
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(157, 92, 255, 0.15)', // More visible hover state
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#CCCCCC', // Brighter icons
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)', // More visible hover state
          },
        },
      },
    },
  },
});

function Pentest() {
  const [apiStatus, setApiStatus] = useState<{
    connected: boolean;
    message: string;
  }>({
    connected: false,
    message: 'Checking API connection...'
  });
  
  const [showAlert, setShowAlert] = useState<boolean>(false);
  
  // Test API connection on startup
  useEffect(() => {
    const testConnection = async () => {
      try {
        const result = await testService.testConnection();
        setApiStatus({
          connected: result.success,
          message: result.message
        });
        
        if (!result.success) {
          setShowAlert(true);
          console.warn('API connection test failed:', result.message);
        } else {
          console.log('API connection successful:', result);
        }
      } catch (error) {
        console.error('Error testing API connection:', error);
        setApiStatus({
          connected: false,
          message: `Error: ${error instanceof Error ? error.message : String(error)}`
        });
        setShowAlert(true);
      }
    };
    
    testConnection();
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainLayout />
      
      {/* API Connection Status Alert */}
      <Snackbar 
        open={showAlert} 
        autoHideDuration={10000} 
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={apiStatus.connected ? 'success' : 'error'}
          variant="filled"
        >
          {apiStatus.connected 
            ? 'Connected to API server'
            : `Cannot connect to API server: ${apiStatus.message}`
          }
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default Pentest;
